import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { Logo } from '../components/Logo';

const EFFECTIVE_DATE = 'February 19, 2026';
const COMPANY = 'CyBoard';
const SUPPORT_EMAIL = 'support@cyboard.games';

export default function TermsOfServicePage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#06060A] text-white" data-testid="terms-of-service-page">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-[#06060A]/80 border-b border-white/[0.06]">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="flex-1">
            <Logo variant="full" size="sm" clickable={false} showTagline={false} />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 pb-20">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>Terms of Service</h1>
        <p className="text-[#71717A] text-sm mb-8">Effective {EFFECTIVE_DATE}</p>

        <article className="space-y-6 text-[#E4E4E7] text-[15px] leading-relaxed">

          <section>
            <p>
              Welcome to {COMPANY}. These Terms of Service ("Terms") govern your access to and use of the
              {' '}{COMPANY} mobile app, website, and related services (the "Service"). By creating an account
              or using the Service, you agree to these Terms. If you do not agree, you must not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">1. Eligibility</h2>
            <p>
              You must be at least 13 years old (or 16 in the European Economic Area) to use {COMPANY}. By
              using the Service you represent that you meet this age requirement and that you have the legal
              authority to agree to these Terms. If you are using the Service on behalf of an organization
              (such as a game shop), you represent that you have authority to bind that organization.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">2. Your Account</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</li>
              <li>Provide accurate registration information and keep it current.</li>
              <li>One account per person. Do not impersonate another person or entity.</li>
              <li>You may delete your account at any time from Profile → Delete my account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">3. Community Conduct</h2>
            <p>{COMPANY} is a community of players, hosts, and shops. To keep it safe and welcoming, you agree NOT to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Harass, threaten, dox, stalk, or bully any other user.</li>
              <li>Post content that is illegal, obscene, hateful, sexually explicit, or that targets a protected class.</li>
              <li>Spam, scam, run pyramid schemes, or post unsolicited promotional content.</li>
              <li>Impersonate another player, host, shop, or {COMPANY} staff.</li>
              <li>Attempt to defeat moderation, ratings, or anti-abuse systems.</li>
              <li>Exploit, probe, or attempt to gain unauthorized access to the Service or other users' accounts.</li>
              <li>Scrape, mirror, or republish any part of the Service without our written permission.</li>
              <li>Use the Service to organize meetings for any unlawful purpose.</li>
            </ul>
            <p>
              We take community safety seriously. Players can be reported (Flag icon on any profile or event)
              or blocked (Profile → Block). We may, in our sole discretion, remove content, warn, suspend, or
              terminate accounts that violate these rules.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">4. User-Generated Content</h2>
            <p>
              You retain ownership of the content you post (event listings, messages, match results, photos,
              etc.). By posting content on {COMPANY} you grant us a worldwide, non-exclusive, royalty-free
              license to host, store, reproduce, display, adapt, and distribute that content solely for the
              purpose of operating and improving the Service. This license ends when you delete the content
              or your account, except where we are legally required to retain it.
            </p>
            <p>
              You are solely responsible for the content you post. Do not post content that infringes any
              third-party intellectual-property right.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">5. Events & In-Person Meetups</h2>
            <p className="font-semibold text-white">{COMPANY} is a discovery and coordination tool, not an organizer.</p>
            <p>
              When you host or join a game session arranged through the Service, you do so at your own risk.
              {COMPANY} does not vet, verify, or insure any host, venue, attendee, or event. We strongly
              recommend you:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Meet new players in public locations when possible (cafés, game shops, community spaces).</li>
              <li>Use the in-app request-to-join flow to vet attendees before sharing your private address.</li>
              <li>Trust your instincts — if anyone makes you uncomfortable, leave the event and report them.</li>
              <li>Do not transport minors to private events without a parent or guardian's knowledge.</li>
            </ul>
            <p>
              You agree that {COMPANY} is not liable for any injury, loss, or damage arising from your
              attendance at an in-person event you discovered through the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">6. Intellectual Property</h2>
            <p>
              The {COMPANY} name, logo, app design, code, and original content are owned by {COMPANY} or its
              licensors and are protected by copyright, trademark, and other laws. Names of board games and
              card games referenced in the app are the property of their respective publishers. {COMPANY} is
              not affiliated with, endorsed by, or sponsored by any game publisher unless explicitly stated.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">7. Subscriptions, Purchases & Refunds</h2>
            <p>
              Currently, all {COMPANY} features are available free of charge during early access. If we
              introduce paid subscriptions or in-app purchases in the future, terms specific to those
              transactions (including billing, auto-renewal, and refund policies) will be presented to you
              before purchase and incorporated into these Terms. In-app purchases on iOS are processed by
              Apple and subject to Apple's terms; refund requests must be made directly to Apple.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">8. Termination</h2>
            <p>
              You may stop using the Service at any time and delete your account from within the app. We
              may suspend or terminate your account, with or without notice, if you violate these Terms,
              pose a safety risk to others, or for any other reason at our reasonable discretion. Upon
              termination, your right to access the Service ends, but the sections that by their nature
              should survive (intellectual property, disclaimers, limitation of liability, governing law,
              dispute resolution) will continue to apply.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">9. Disclaimers</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, NON-INFRINGEMENT, OR AVAILABILITY. WE DO NOT WARRANT THAT THE SERVICE WILL BE
              UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY.toUpperCase()} AND ITS OFFICERS, DIRECTORS,
              EMPLOYEES, AND CONTRACTORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF
              OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING OUT
              OF OR RELATING TO THESE TERMS OR THE SERVICE IS LIMITED TO THE GREATER OF (A) THE AMOUNT YOU
              HAVE PAID US IN THE 12 MONTHS PRECEDING THE CLAIM OR (B) US$50.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">11. Indemnification</h2>
            <p>
              You agree to indemnify and hold {COMPANY} harmless from any claims, damages, liabilities,
              and expenses (including reasonable attorneys' fees) arising out of your use of the Service,
              your content, your violation of these Terms, or your violation of any third-party right.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">12. Governing Law & Disputes</h2>
            <p>
              These Terms are governed by the laws of the State of Texas, United States, without regard to
              its conflict-of-laws provisions. Any dispute arising out of or related to these Terms or the
              Service will be resolved exclusively in the state or federal courts located in Texas, and you
              consent to personal jurisdiction in those courts. Where required by applicable consumer law,
              you may also bring claims in your country of residence.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">13. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time. We will post the updated Terms here with a new
              "Effective" date. Material changes will be communicated to you in-app or by email. Continued
              use of the Service after the change takes effect constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">14. Contact</h2>
            <p>
              Questions about these Terms? Email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#9D4CDD] underline">{SUPPORT_EMAIL}</a>.
            </p>
          </section>
        </article>
      </main>
    </div>
  );
}
